import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import logger from '../utils/logger';
import { Printer, Phone, Mail, MapPin, Check, ArrowLeft, Edit, CheckCircle, XCircle, Wallet, RotateCcw, Truck, Package, CreditCard, Smartphone, Landmark, FileText, Banknote, X } from 'lucide-react';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';

const BusinessInvoiceView = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : (outletContext?.canManageInvoices ?? true);
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();
  
  const { id } = useParams();
  const printRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [returns, setReturns] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectId]);

  const loadData = async () => {
    try {
      const [invoiceRes, settingsRes] = await Promise.all([
        api.get(`${apiBase}/invoices/${id}`),
        api.get(`${apiBase}/settings`).catch(err => {
          logger.warn('Settings load failed:', err.response?.status, err.response?.data, err.message);
          return { data: null };
        })
      ]);
      logger.log('Settings loaded:', settingsRes.data);
      setInvoice(invoiceRes.data);
      setSettings(settingsRes.data);
      // Charger BL et avoirs liés
      try {
        const [dlRes, retRes] = await Promise.all([
          api.get(`${apiBase}/delivery-notes?invoice_id=${id}`),
          api.get(`${apiBase}/returns?invoice_id=${id}`)
        ]);
        setDeliveryNotes(dlRes.data);
        setReturns(retRes.data);
      } catch (e) { /* silently fail if endpoints not ready */ }
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoice = async () => {
    try {
      const res = await api.get(`${apiBase}/invoices/${id}`);
      setInvoice(res.data);
    } catch (err) {
      logger.error(err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`${apiBase}/invoices/${id}`, { statut: newStatus });
      loadInvoice();
    } catch (err) {
      logger.error(err);
    }
  };

  const handleValidate = async () => {
    if (!await confirm(`Valider cette facture ?\n\nCette action va :\n• Changer le statut en "Envoyée"\n• Déduire les quantités du stock des produits\n• Créer un bon de livraison`)) return;
    try {
      await api.post(`${apiBase}/invoices/${id}/validate`);
      // Créer automatiquement un bon de livraison
      try {
        await api.post(`${apiBase}/delivery-notes`, { invoice_id: parseInt(id) });
      } catch (e) { logger.warn('BL auto-creation failed:', e); }
      toast.success('Facture validée ! Les stocks ont été mis à jour et un bon de livraison a été créé.');
      loadData();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la validation');
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-gray-100 text-gray-600',
      envoyee: 'bg-blue-100 text-blue-600',
      partiellement_payee: 'bg-amber-100 text-amber-600',
      payee: 'bg-green-100 text-green-600',
      annulee: 'bg-red-100 text-red-600'
    };
    const labels = {
      brouillon: 'Brouillon',
      envoyee: 'Envoyée',
      partiellement_payee: 'Partiellement payée',
      payee: 'Payée',
      annulee: 'Annulée'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[statut] || styles.brouillon}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Facture introuvable</p>
        <Link to={`${routeBase}/invoices`} className="text-indigo-600 hover:underline mt-2 block">
          Retour aux factures
        </Link>
      </div>
    );
  }

  const total = parseFloat(invoice.total) || 0;
  const paye = parseFloat(invoice.montant_paye) || 0;
  const reste = total - paye;

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Header avec actions - Masqué à l'impression */}
      <div className="print:hidden bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`${routeBase}/invoices`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition flex items-center gap-1">
                <ArrowLeft size={16} /> Retour
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{invoice.numero_facture}</h1>
                <p className="text-sm text-gray-500">
                  {invoice.type === 'proforma' ? 'Facture proforma' : 'Facture'} • {new Date(invoice.date_facture).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(invoice.statut)}
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
              >
                <Printer size={16} className="inline" /> Imprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:block">
          {/* Zone imprimable - Facture */}
          <div className="lg:col-span-2 print:col-span-3" ref={printRef}>
            <div className="bg-white rounded-xl shadow-lg print:shadow-none print:rounded-none overflow-hidden">
              
              {/* En-tête entreprise */}
              <div className="p-8 border-b print:p-6">
                <div className="flex justify-between items-start">
                  {/* Logo et infos entreprise */}
                  <div className="flex items-start gap-4">
                    {settings?.logo && (
                      <img 
                        src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`} 
                        alt="Logo" 
                        className="w-20 h-20 object-contain rounded-lg"
                      />
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">{settings?.nom_entreprise || 'Mon Entreprise'}</h2>
                      {settings?.adresse && <p className="text-gray-600">{settings.adresse}</p>}
                      {settings?.telephone && <p className="text-gray-600"><Phone size={14} className="inline" /> {settings.telephone}</p>}
                      {settings?.email && <p className="text-gray-600"><Mail size={14} className="inline" /> {settings.email}</p>}
                      {settings?.numero_registre && (
                        <p className="text-gray-500 text-sm mt-1">RCCM: {settings.numero_registre}</p>
                      )}
                      {settings?.numero_fiscal && (
                        <p className="text-gray-500 text-sm">NIF: {settings.numero_fiscal}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Titre et numéro facture */}
                  <div className="text-right">
                    <h1 className="text-3xl font-bold text-indigo-600">
                      {invoice.type === 'proforma' ? 'PROFORMA' : 'FACTURE'}
                    </h1>
                    <p className="text-lg text-gray-600 font-medium">{invoice.numero_facture}</p>
                    <div className="mt-3 text-sm text-gray-500">
                      <p>Date: <span className="text-gray-700 font-medium">{new Date(invoice.date_facture).toLocaleDateString('fr-FR')}</span></p>
                      {invoice.date_echeance && (
                        <p>Échéance: <span className="text-gray-700 font-medium">{new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}</span></p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations client */}
              <div className="p-8 bg-gray-50 print:p-6 print:bg-gray-100">
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Facturé à</h3>
                    <div className="text-gray-800">
                      <p className="text-lg font-semibold">{invoice.client_nom || 'Client'}</p>
                      {invoice.client_adresse && <p className="text-gray-600">{invoice.client_adresse}</p>}
                      {invoice.client_telephone && <p className="text-gray-600"><Smartphone size={14} className="inline" /> {invoice.client_telephone}</p>}
                      {invoice.client_email && <p className="text-gray-600"><Mail size={14} className="inline" /> {invoice.client_email}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    {invoice.statut === 'payee' && (
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 font-bold rounded-lg text-lg flex items-center gap-1">
                        <CheckCircle size={16} className="inline" /> PAYÉE
                      </span>
                    )}
                    {invoice.statut === 'partiellement_payee' && (
                      <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 font-bold rounded-lg">
                        PARTIELLEMENT PAYÉE
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tableau des lignes */}
              <div className="p-8 print:p-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left pb-3 text-gray-500 font-semibold text-sm uppercase">Description</th>
                      <th className="text-center pb-3 text-gray-500 font-semibold text-sm uppercase w-20">Qté</th>
                      <th className="text-right pb-3 text-gray-500 font-semibold text-sm uppercase w-32">Prix unit.</th>
                      <th className="text-right pb-3 text-gray-500 font-semibold text-sm uppercase w-32">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.items || []).map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-4">
                          <div className="text-gray-800">{item.description}</div>
                          {item.product_description && (
                            <div className="text-xs text-gray-500 mt-1">{item.product_description}</div>
                          )}
                          {item.product_categorie && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">{item.product_categorie}</span>
                          )}
                        </td>
                        <td className="py-4 text-center text-gray-600">{item.quantite}</td>
                        <td className="py-4 text-right text-gray-600">{parseFloat(item.prix_unitaire).toLocaleString('fr-FR')} XOF</td>
                        <td className="py-4 text-right font-medium text-gray-800">
                          {(parseFloat(item.quantite) * parseFloat(item.prix_unitaire)).toLocaleString('fr-FR')} XOF
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totaux */}
                <div className="mt-6 flex justify-end">
                  <div className="w-72">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Sous-total</span>
                      <span className="font-medium">{parseFloat(invoice.sous_total).toLocaleString('fr-FR')} XOF</span>
                    </div>
                    {parseFloat(invoice.tva || 0) > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">TVA</span>
                        <span className="font-medium">{parseFloat(invoice.tva).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    {parseFloat(invoice.remise || 0) > 0 && (
                      <div className="flex justify-between py-2 border-b text-red-600">
                        <span>Remise</span>
                        <span className="font-medium">-{parseFloat(invoice.remise).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 bg-indigo-50 px-3 rounded-lg mt-2">
                      <span className="text-xl font-bold text-gray-800">TOTAL</span>
                      <span className="text-xl font-bold text-indigo-600">{total.toLocaleString('fr-FR')} XOF</span>
                    </div>
                    {paye > 0 && (
                      <>
                        <div className="flex justify-between py-2 text-green-600 mt-2">
                          <span>Montant payé</span>
                          <span className="font-medium">-{paye.toLocaleString('fr-FR')} XOF</span>
                        </div>
                        {reste > 0 && (
                          <div className="flex justify-between py-2 text-red-600 font-bold">
                            <span>Reste à payer</span>
                            <span>{reste.toLocaleString('fr-FR')} XOF</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes et conditions */}
              {(invoice.notes || invoice.conditions || settings?.conditions_paiement || settings?.mentions_legales) && (
                <div className="p-8 bg-gray-50 border-t print:p-6">
                  {invoice.notes && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{invoice.notes}</p>
                    </div>
                  )}
                  {(invoice.conditions || settings?.conditions_paiement) && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-1">Conditions de paiement</h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{invoice.conditions || settings.conditions_paiement}</p>
                    </div>
                  )}
                  {settings?.mentions_legales && (
                    <div className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-200">
                      {settings.mentions_legales}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Historique des paiements - Visible aussi à l'impression */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg mt-6 p-6 print:shadow-none print:mt-4 print:border-t print:rounded-none">
                <h3 className="font-semibold text-gray-800 mb-4">Historique des paiements</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left pb-2 text-gray-500">Date</th>
                      <th className="text-left pb-2 text-gray-500">Mode</th>
                      <th className="text-right pb-2 text-gray-500">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((payment, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2">{new Date(payment.date_paiement).toLocaleDateString('fr-FR')}</td>
                        <td className="py-2">{payment.mode_paiement}</td>
                        <td className="py-2 text-right font-medium text-green-600">
                          {parseFloat(payment.montant).toLocaleString('fr-FR')} XOF
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sidebar Actions - Masquée à l'impression */}
          <div className="lg:col-span-1 print:hidden">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              {/* État du paiement */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">État du paiement</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total facture</span>
                    <span className="font-medium">{total.toLocaleString('fr-FR')} XOF</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Déjà payé</span>
                    <span className="font-medium">{paye.toLocaleString('fr-FR')} XOF</span>
                  </div>
                  {reste > 0 && (
                    <div className="flex justify-between text-red-600 font-bold border-t pt-2">
                      <span>Reste à payer</span>
                      <span>{reste.toLocaleString('fr-FR')} XOF</span>
                    </div>
                  )}
                </div>
                
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${Math.min((paye / total) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {total > 0 ? Math.round((paye / total) * 100) : 0}% payé
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {invoice.statut === 'brouillon' && canManage && (
                  <>
                    <Link
                      to={`${routeBase}/invoices/${id}/edit`}
                      className="w-full py-2 bg-amber-100 text-amber-600 rounded-xl hover:bg-amber-200 transition flex items-center justify-center gap-2"
                    >
                      <Edit size={16} className="inline" /> Modifier
                    </Link>
                    <button
                      onClick={handleValidate}
                      className="w-full py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold"
                    >
                      <CheckCircle size={16} className="inline" /> Valider la facture
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      La validation déduira les produits du stock
                    </p>
                  </>
                )}

                {['envoyee', 'partiellement_payee'].includes(invoice.statut) && reste > 0 && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-semibold"
                  >
                    <Wallet size={16} className="inline" /> Enregistrer un paiement
                  </button>
                )}

                {['envoyee', 'partiellement_payee', 'payee'].includes(invoice.statut) && canManage && (
                  <Link
                    to={`${routeBase}/returns/new?invoice_id=${id}`}
                    className="w-full py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={16} className="inline" /> Retour de produit
                  </Link>
                )}

                <button
                  onClick={handlePrint}
                  className="w-full py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition"
                >
                  <Printer size={16} className="inline" /> Imprimer
                </button>

                {invoice.statut !== 'annulee' && invoice.statut !== 'payee' && (
                  <button
                    onClick={async () => {
                      if (await confirm('Annuler cette facture ?')) {
                        handleStatusChange('annulee');
                      }
                    }}
                    className="w-full py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition"
                  >
                    <XCircle size={16} className="inline" /> Annuler la facture
                  </button>
                )}
              </div>

              {/* Bons de livraison liés */}
              {deliveryNotes.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-1"><Package size={16} className="inline" /> Bons de livraison</h3>
                  <div className="space-y-2">
                    {deliveryNotes.map(dn => (
                      <Link key={dn.id} to={`${routeBase}/delivery-notes/${dn.id}`}
                        className="block p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-700">{dn.numero_bl}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            dn.statut === 'livre' ? 'bg-green-100 text-green-700' :
                            dn.statut === 'livre_partiel' ? 'bg-orange-100 text-orange-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {dn.statut === 'livre' ? 'Livré' : dn.statut === 'livre_partiel' ? 'Partiel' : 'En prép.'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{new Date(dn.date_livraison).toLocaleDateString('fr-FR')}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Avoirs liés */}
              {returns.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-1"><RotateCcw size={16} className="inline" /> Avoirs / Retours</h3>
                  <div className="space-y-2">
                    {returns.map(ret => (
                      <Link key={ret.id} to={`${routeBase}/returns/${ret.id}`}
                        className="block p-3 bg-red-50 rounded-xl hover:bg-red-100 transition">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-red-700">{ret.numero_avoir}</span>
                          <span className="text-sm font-bold text-red-600">{parseFloat(ret.total).toLocaleString()} XOF</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{new Date(ret.date_retour).toLocaleDateString('fr-FR')}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          invoice={invoice}
          reste={reste}
          apiBase={apiBase}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            loadInvoice();
          }}
        />
      )}

      {/* Styles d'impression */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:col-span-3 {
            grid-column: span 3 / span 3;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:rounded-none {
            border-radius: 0 !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:p-6 {
            padding: 1.5rem !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .print\\:mt-4 {
            margin-top: 1rem !important;
          }
          .print\\:border-t {
            border-top: 1px solid #e5e7eb !important;
          }
        }
      `}</style>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ invoice, reste, apiBase = '/business', onClose, onSuccess }) => {
  const clientCredit = parseFloat(invoice.client_solde_credit) || 0;
  const [useCredit, setUseCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState(0);
  const maxCredit = Math.min(clientCredit, reste);

  const resteApresCredit = useCredit ? Math.max(0, reste - creditAmount) : reste;

  const [form, setForm] = useState({
    montant: reste,
    mode_paiement: 'especes',
    reference: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Mettre à jour le montant quand le crédit change
  useEffect(() => {
    if (useCredit) {
      setForm(prev => ({ ...prev, montant: resteApresCredit }));
    } else {
      setForm(prev => ({ ...prev, montant: reste }));
    }
  }, [useCredit, creditAmount, reste, resteApresCredit]);

  const handleToggleCredit = () => {
    if (!useCredit) {
      // Activer le crédit  : utiliser le max possible par défaut
      setCreditAmount(maxCredit);
      setUseCredit(true);
    } else {
      setUseCredit(false);
      setCreditAmount(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const montantEspeces = parseFloat(form.montant) || 0;
    const montantCredit = useCredit ? creditAmount : 0;
    const totalPaiement = montantEspeces + montantCredit;

    if (totalPaiement <= 0) {
      toast.warning('Le montant total du paiement doit être supérieur à 0');
      return;
    }

    if (montantCredit > clientCredit) {
      toast.warning('Le montant crédit dépasse le solde disponible du client');
      return;
    }

    setSaving(true);
    try {
      if (montantEspeces > 0 || montantCredit > 0) {
        await api.post(`${apiBase}/payments`, {
          invoice_id: invoice.id,
          client_id: invoice.client_id,
          type: 'encaissement',
          montant: montantEspeces,
          mode_paiement: form.mode_paiement,
          reference: form.reference,
          notes: form.notes,
          credit_amount: montantCredit
        });
      }
      onSuccess();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 my-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Enregistrer un paiement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        
        <div className="mb-4 p-3 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Facture {invoice.numero_facture}</div>
          <div className="text-lg font-bold text-red-600">Reste à payer: {reste.toLocaleString()} XOF</div>
        </div>

        {/* Crédit client disponible */}
        {clientCredit > 0 && invoice.client_id && (
          <div className={`mb-4 p-3 rounded-xl border-2 transition-all ${useCredit ? 'border-green-400 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg"><Wallet size={16} /></span>
                <div>
                  <div className="text-sm font-semibold text-gray-800">Crédit client disponible</div>
                  <div className="text-xs text-gray-500">{invoice.client_nom}</div>
                </div>
              </div>
              <span className="text-lg font-bold text-green-600">{clientCredit.toLocaleString()} XOF</span>
            </div>
            <button
              type="button"
              onClick={handleToggleCredit}
              className={`w-full py-2 rounded-xl text-sm font-semibold transition ${useCredit ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
            >
              {useCredit ? <><CheckCircle size={16} className="inline" /> Crédit utilisé</> : '→ Utiliser le crédit pour payer'}
            </button>
            {useCredit && (
              <div className="mt-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">Montant crédit à utiliser</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max={maxCredit}
                    step="1"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(Math.min(parseFloat(e.target.value) || 0, maxCredit))}
                    className="flex-1 border rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500"
                  />
                  <button type="button" onClick={() => setCreditAmount(maxCredit)} className="px-3 py-2 bg-green-100 text-green-700 rounded-xl text-xs font-semibold hover:bg-green-200">
                    Max
                  </button>
                </div>
                {creditAmount >= reste && (
                  <p className="text-xs text-green-600 font-medium"><CheckCircle size={14} className="inline" /> Le crédit couvre la totalité de la facture</p>
                )}
                {creditAmount > 0 && creditAmount < reste && (
                  <p className="text-xs text-amber-600">Paiement complémentaire nécessaire : {resteApresCredit.toLocaleString()} XOF</p>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Montant en espèces/autre (seulement si nécessaire) */}
          {(!useCredit || resteApresCredit > 0) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {useCredit ? `Montant complémentaire *` : 'Montant reçu *'}
                </label>
                <input
                  type="number"
                  min="0"
                  max={resteApresCredit}
                  value={form.montant}
                  onChange={(e) => setForm({ ...form, montant: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  required={!useCredit || resteApresCredit > 0}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                <select
                  value={form.mode_paiement}
                  onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="especes">Espèces</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="virement">Virement bancaire</option>
                  <option value="cheque">Chèque</option>
                  <option value="carte">Carte bancaire</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
                <input
                  type="text"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="N° transaction, chèque..."
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Résumé du paiement */}
          {useCredit && creditAmount > 0 && (
            <div className="p-3 bg-gray-50 rounded-xl text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500"><Wallet size={14} className="inline" /> Paiement par crédit client</span>
                <span className="font-semibold text-green-600">{creditAmount.toLocaleString()} XOF</span>
              </div>
              {resteApresCredit > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500"><Banknote size={14} className="inline" /> Paiement complémentaire</span>
                  <span className="font-semibold">{(parseFloat(form.montant) || 0).toLocaleString()} XOF</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="text-gray-700 font-medium">Total</span>
                <span className="font-bold">{(creditAmount + (parseFloat(form.montant) || 0)).toLocaleString()} XOF</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : <><Wallet size={16} className="inline" /> Confirmer</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessInvoiceView;
